import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Info, AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";
import { qk } from "@/lib/query";
import { local } from "@/lib/storage";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";

const KEY = "baze:dismissed-announcements";

export function AnnouncementBanner() {
  const { data } = useQuery({ queryKey: qk.announcements, queryFn: () => api.public.announcements(), staleTime: 5 * 60_000, retry: false });
  const [dismissed, setDismissed] = useState<string[]>(() => local.get<string[]>(KEY, []));
  const now = Date.now();
  const visible = (data ?? []).filter(
    (a) => !dismissed.includes(a.id) && new Date(a.startsAt).getTime() <= now && (!a.endsAt || new Date(a.endsAt).getTime() > now),
  );
  if (!visible.length) return null;
  const dismiss = (id: string) => {
    const next = [...dismissed, id].slice(-50);
    setDismissed(next);
    local.set(KEY, next);
  };
  return (
    <div className="no-print">
      {visible.map((a) => {
        const Icon = a.level === "info" ? Info : AlertTriangle;
        return (
          <div
            key={a.id}
            role={a.level === "critical" ? "alert" : "status"}
            className={cn(
              "flex items-center gap-3 px-4 py-2.5 text-sm",
              a.level === "info" && "bg-brand-navy text-white",
              a.level === "warning" && "bg-warning-soft text-amber-950",
              a.level === "critical" && "bg-danger text-white",
            )}
          >
            <div className="mx-auto flex w-full max-w-[1120px] items-center gap-3">
              <Icon className="size-4 shrink-0" aria-hidden="true" />
              <p className="min-w-0 flex-1 font-medium">{a.message}</p>
              {a.dismissible ? (
                <button type="button" onClick={() => dismiss(a.id)} className="-mr-2 grid size-9 shrink-0 place-items-center rounded-full hover:bg-white/15" aria-label={t.shell.announcementDismiss}>
                  <X className="size-4" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
